import classNames from "classnames";
import {LazyLoadImageWrapper} from "components";
import {useAppDispatch, useAppSelector, useOpenLink} from "hooks";
import useUserBehaviorTrackHook from "hooks/useUserBehaviorTrackHook";
import {getSingleCompanyDone} from "store/company/companyActions";
import {setTrackedEventsData} from "store/events/eventTrackingActions";
import {setActiveAccordionOnFilter, setFilterForCompanies} from "store/filter/filterActions";
import {getIndustries} from "store/search/searchAsyncActions";
import {industryOptionsSelector} from "store/search/searchSelectors";

import css from "./infoSection.module.scss";
import {TCompanyInformationProps} from "mapx-components/Cards/CompanyCard/CompanyInformation/types";
import {companyFiltersSelector} from "store/filter/filterSelectors";
import {STIndustry} from "api/filterOptionsApi/IndustryApi/types";
import {MouseEvent, useMemo} from "react";

export default function CompanyInformation({
	company,
	from,
	isRecommended,
	position,
}: TCompanyInformationProps) {
	const openLink = useOpenLink();
	const dispatch = useAppDispatch();
	const {itemEventDetails} = useUserBehaviorTrackHook({
		itemType: "company",
		defaultEvent: "details_viewed",
		itemId: company.id,
		source: from,
		isRecommended,
		position,
	});

	const handleItemClick = (event: MouseEvent<HTMLDivElement>) => {
		event.preventDefault();

		dispatch(setTrackedEventsData(itemEventDetails));

		if (event.button === 0) {
			dispatch(getSingleCompanyDone(company));
			const link = `/company/${company.id}${isRecommended ? "?recommended=true" : ""}`;
			openLink(event, link);
		}
	};

	/**
	 ** SPECIALTY FILTER MATCH & CONTROL
	 */
	const {industries} = useAppSelector(companyFiltersSelector);
	const industryOptions = useAppSelector(industryOptionsSelector);

	const handleSelectedIndustries = (item: Nullable<STIndustry>) => {
		if (industryOptions.length === 0) {
			dispatch(setActiveAccordionOnFilter("Industry"));

			dispatch(getIndustries());
		}

		if (!item) return;

		const current = industries ? [...industries] : [];
		let updatedIds = [];

		if (current.includes(item.id)) {
			updatedIds = [...current.filter((id) => item.id !== id)];
		} else {
			updatedIds = [...current, item.id];
		}

		dispatch(setFilterForCompanies({type: "industries", values: updatedIds}));
	};

	const headQuarterInfo = useMemo(() => {
		let headquarter = company.headquarters;

		if (headquarter === ", ") {
			headquarter = "";
		}

		let headquarterCountry = company?.headquarters_country?.name;

		if (!headquarterCountry) {
			headquarterCountry = "";
		}

		return headquarter && headquarter?.length > 0
			? headquarter + ", " + headquarterCountry
			: headquarterCountry;
	}, [company]);

	return (
		<div className={css.infoFirstRow}>
			<div onClick={handleItemClick}>
				<LazyLoadImageWrapper
					className={css.logo}
					image={
						company.logo_url !== null
							? company.logo_url
							: "https://storage.googleapis.com/nebula-static/logos100x100/empty_logo.png"
					}
					alt={company.name}
					style={{background: `url(${company.logo_url})`}}
				/>
			</div>
			<div>
				<div className={css.infoTitle} onClick={handleItemClick}>
					<a
						href={`/company/${company.id}${isRecommended ? "?recommended=true" : ""}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						{company.name}
					</a>
				</div>

				<div className={css.infoDetail}>{headQuarterInfo}</div>

				{company?.industry !== null && company?.industry?.id !== undefined && (
					<div className={css.infoCategory}>
						<span
							className={classNames(css.infoCategoryButton, {
								// [css.selected]: industries?.includes(item.industry.id),
							})}
							onClick={() => handleSelectedIndustries(company?.industry)}
						>
							{company.industry?.name}
						</span>
					</div>
				)}
			</div>
		</div>
	);
}
